import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'step',
    'nextButton',
    'prevLink',
    'otpForm',
    'sandboxHint',
    'stepIndicator',
    'stepTitle',
    'stepperSegment',
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: Number,
    otpUrl: String,
    docTitles: Array,
  }

  connect() {
    this.scrolledSteps = new Set()
    // Intro step has nothing to read — auto-eligible.
    this.scrolledSteps.add(0)
    this.showStep(0)
  }

  docScrolled() {
    this.scrolledSteps.add(this.currentStepValue)
    this.enableNextButton()
    this.refreshReadState()
  }

  nextStep() {
    if (this.currentStepValue < this.totalStepsValue - 1) {
      this.showStep(this.currentStepValue + 1)
    }
  }

  prevStep() {
    if (this.currentStepValue > 0) {
      this.showStep(this.currentStepValue - 1)
    }
  }

  async sendOtp() {
    const response = await fetch(this.otpUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
    })

    if (response.ok) {
      const data = await response.json()
      if (data.sandbox_code && this.hasSandboxHintTarget) {
        this.sandboxHintTarget.textContent = data.sandbox_code
        this.sandboxHintTarget
          .closest('[data-sandbox-wrapper]')
          .classList.remove('hidden')
      }
    }

    this.showStep(this.totalStepsValue - 1)

    if (this.hasOtpFormTarget) {
      this.otpFormTarget.classList.remove('hidden')
      this.otpFormTarget.classList.add('flex')
    }
  }

  showStep(index) {
    this.currentStepValue = index

    this.stepTargets.forEach((step, i) => {
      step.classList.toggle('hidden', i !== index)
    })

    if (this.scrolledSteps.has(index)) {
      this.enableNextButton()
    } else {
      this.disableNextButton()
    }

    this.updatePrevLink()
    this.updateStepIndicator()
    this.updateStepperSegments()
    this.refreshReadState()
  }

  enableNextButton() {
    const btn = this.currentNextButton()
    if (!btn) return
    btn.disabled = false
  }

  disableNextButton() {
    const btn = this.currentNextButton()
    if (!btn) return
    btn.disabled = true
  }

  currentNextButton() {
    return this.nextButtonTargets[this.currentStepValue]
  }

  updatePrevLink() {
    // prevLink targets start at step 1 — intro has none.
    const prevLinkIndex = this.currentStepValue - 1
    this.prevLinkTargets.forEach((link, i) => {
      const visible = i === prevLinkIndex && this.currentStepValue > 0
      link.classList.toggle('hidden', !visible)
    })
  }

  updateStepIndicator() {
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTarget.textContent =
        `${this.currentStepValue + 1} / ${this.totalStepsValue}`
    }
    if (this.hasStepTitleTarget) {
      this.stepTitleTarget.textContent =
        this.docTitlesValue[this.currentStepValue] || ''
    }
  }

  updateStepperSegments() {
    this.stepperSegmentTargets.forEach((segment, i) => {
      const active = i <= this.currentStepValue
      segment.classList.toggle('bg-primary', active)
      segment.classList.toggle('bg-base-300', !active)
    })
  }

  refreshReadState() {
    const stepElement = this.stepTargets[this.currentStepValue]
    if (!stepElement) return

    const unread = stepElement.querySelector(
      '[data-signing-flow-target="readStateUnread"]'
    )
    const read = stepElement.querySelector(
      '[data-signing-flow-target="readStateRead"]'
    )
    if (!unread || !read) return

    const isRead = this.scrolledSteps.has(this.currentStepValue)
    unread.classList.toggle('hidden', isRead)
    read.classList.toggle('hidden', !isRead)
  }
}
